const JOYSTICK_DEADZONE = 0.14;

export class InputState {
  private readonly pressed = new Set<string>();
  private readonly touchJoystickBase = document.querySelector<HTMLDivElement>("#touch-joystick");
  private readonly touchJoystickKnob = document.querySelector<HTMLDivElement>("#touch-joystick-knob");
  private touchPointerId: number | null = null;
  private touchVector = { x: 0, y: 0 };
  private touchMaxRadiusPx = 44;

  attach(): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    this.touchJoystickBase?.addEventListener("pointerdown", this.onJoystickDown);
    this.touchJoystickBase?.addEventListener("pointermove", this.onJoystickMove);
    this.touchJoystickBase?.addEventListener("pointerup", this.onJoystickUp);
    this.touchJoystickBase?.addEventListener("pointercancel", this.onJoystickUp);
    this.touchJoystickBase?.addEventListener("lostpointercapture", this.onJoystickUp);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    this.touchJoystickBase?.removeEventListener("pointerdown", this.onJoystickDown);
    this.touchJoystickBase?.removeEventListener("pointermove", this.onJoystickMove);
    this.touchJoystickBase?.removeEventListener("pointerup", this.onJoystickUp);
    this.touchJoystickBase?.removeEventListener("pointercancel", this.onJoystickUp);
    this.touchJoystickBase?.removeEventListener("lostpointercapture", this.onJoystickUp);
  }

  axis(): { x: number; y: number } {
    const keyboardAxis = keyboardAxisFrom(this.pressed);
    const touchAxis = this.touchVector;
    if (keyboardAxis.x === 0 && keyboardAxis.y === 0) {
      return touchAxis;
    }
    return clampUnitVector({
      x: keyboardAxis.x + touchAxis.x,
      y: keyboardAxis.y + touchAxis.y,
    });
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (isEditableTarget(event.target)) return;
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Space"].includes(event.code)) {
      event.preventDefault();
    }
    this.pressed.add(event.code);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (isEditableTarget(event.target)) return;
    this.pressed.delete(event.code);
  };

  private readonly onBlur = (): void => {
    this.pressed.clear();
    this.resetTouchJoystick();
  };

  private readonly onJoystickDown = (event: PointerEvent): void => {
    if (!this.touchJoystickBase) return;
    event.preventDefault();
    this.touchPointerId = event.pointerId;
    this.touchMaxRadiusPx = joystickRadius(this.touchJoystickBase);
    this.touchJoystickBase.setPointerCapture(event.pointerId);
    this.updateTouchVectorFromEvent(event);
  };

  private readonly onJoystickMove = (event: PointerEvent): void => {
    if (this.touchPointerId === null || event.pointerId !== this.touchPointerId) return;
    event.preventDefault();
    this.updateTouchVectorFromEvent(event);
  };

  private readonly onJoystickUp = (event: PointerEvent): void => {
    if (this.touchPointerId === null || event.pointerId !== this.touchPointerId) return;
    event.preventDefault();
    if (this.touchJoystickBase?.hasPointerCapture(event.pointerId)) {
      this.touchJoystickBase.releasePointerCapture(event.pointerId);
    }
    this.resetTouchJoystick();
  };

  private updateTouchVectorFromEvent(event: PointerEvent): void {
    const base = this.touchJoystickBase;
    if (!base) return;
    const rect = base.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const rawX = event.clientX - centerX;
    const rawY = event.clientY - centerY;
    const maxRadius = this.touchMaxRadiusPx;
    const distance = Math.hypot(rawX, rawY);
    const scale = distance > maxRadius ? maxRadius / distance : 1;
    const offsetX = rawX * scale;
    const offsetY = rawY * scale;

    if (this.touchJoystickKnob) {
      this.touchJoystickKnob.style.transform = `translate(${offsetX.toFixed(1)}px, ${offsetY.toFixed(1)}px)`;
    }

    const normalizedX = maxRadius > 0 ? offsetX / maxRadius : 0;
    const normalizedY = maxRadius > 0 ? offsetY / maxRadius : 0;
    const magnitude = Math.hypot(normalizedX, normalizedY);
    if (magnitude <= JOYSTICK_DEADZONE) {
      this.touchVector = { x: 0, y: 0 };
      return;
    }
    const normalizedMagnitude = Math.min(1, (magnitude - JOYSTICK_DEADZONE) / (1 - JOYSTICK_DEADZONE));
    this.touchVector = clampUnitVector({
      x: (normalizedX / magnitude) * normalizedMagnitude,
      y: (normalizedY / magnitude) * normalizedMagnitude,
    });
  }

  private resetTouchJoystick(): void {
    this.touchPointerId = null;
    this.touchVector = { x: 0, y: 0 };
    if (this.touchJoystickKnob) {
      this.touchJoystickKnob.style.transform = "translate(0px, 0px)";
    }
  }
}

function keyboardAxisFrom(pressed: Set<string>): { x: number; y: number } {
  const left = pressed.has("ArrowLeft") || pressed.has("KeyA");
  const right = pressed.has("ArrowRight") || pressed.has("KeyD");
  const up = pressed.has("ArrowUp") || pressed.has("KeyW");
  const down = pressed.has("ArrowDown") || pressed.has("KeyS");
  let x = Number(right) - Number(left);
  let y = Number(down) - Number(up);
  if (x !== 0 && y !== 0) {
    const normal = Math.SQRT1_2;
    x *= normal;
    y *= normal;
  }
  return { x, y };
}

function clampUnitVector(vector: { x: number; y: number }): { x: number; y: number } {
  const magnitude = Math.hypot(vector.x, vector.y);
  if (magnitude <= 1) return vector;
  if (magnitude === 0) return { x: 0, y: 0 };
  return { x: vector.x / magnitude, y: vector.y / magnitude };
}

function joystickRadius(base: HTMLDivElement): number {
  const rect = base.getBoundingClientRect();
  return Math.max(24, Math.min(rect.width, rect.height) * 0.5 - 12);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}
